/**
 * The pure decision behind `openInvoice`'s wire payload — kept separate from
 * `TablePage.tsx` so the tri-state rule (contract amendment to E10, tier-2 P2
 * rework) has one place it is decided and one place it is tested.
 *
 * `priceTierId` is tri-state end to end:
 * - the field ABSENT from the payload means "the waiter never touched the
 *   picker" — the server may auto-apply an in-window `autoApply` tier.
 * - `null` means an explicit "no tier" answer — the waiter tapped "Base
 *   prices" (or any tier chip) at least once, and the server MUST NOT
 *   auto-apply on top of that choice.
 * - a UUID names the tier the waiter picked.
 *
 * `guestCount` stays binary (absent vs a positive integer): a blank cover
 * count is "nobody counted", never `0`, and the DTO refuses `0` anyway.
 *
 * F1 (unit 4) added the CHANNEL, and it changes the shape rather than adding a
 * field: an off-premise bill carries NO `tableId` at all, because a takeaway
 * bag sits at no table and a takeaway bill that carried one would distort
 * exactly the per-table figures — occupancy, average ticket — that the
 * dummy-table workaround distorted, which is what this unit exists to remove.
 * The server refuses the mismatch either way (`RESTAURANT_CHANNEL_TABLES_
 * INVALID`); this keeps the till from sending one.
 */

export interface OpenInvoicePayloadInput {
  /** Empty on an off-premise bill, which sits at no table. */
  tableId: string;
  /** Absent is DINE_IN — every bill the till sent before unit 4. */
  channel?: "DINE_IN" | "TAKEAWAY" | "DELIVERY";
  /** B9: who the food is for. Blank fields are omitted, never sent empty. */
  customerName?: string;
  customerPhone?: string;
  deliveryAddress?: string;
  /**
   * B46: the fee for THIS delivery, as typed. Blank means "use the store's
   * default", which is a different answer from an explicit 0 (this one is
   * free), so a blank field is omitted rather than sent as zero. Sent rounded
   * to 2 dp (G13): a typed `12.345` is money, and the server stores money.
   */
  deliveryFeeRaw?: string;
  /**
   * F2: WHO BROUGHT THE CUSTOMER — `null` is "direct", the shop's own phone.
   *
   * Sent on BOTH off-premise channels since T7 (2026-10-06): a platform's
   * pickup order is a takeaway with a commission on it, and refusing the id
   * on TAKEAWAY made every such bag a direct order on the margin report. It is
   * still dropped on DINE_IN, which is the one channel the server refuses it
   * on (`RESTAURANT_AGGREGATOR_CHANNEL_INVALID`, C2) — the till should not be
   * the one asking.
   *
   * The RATE is deliberately absent — it is read off the aggregator record
   * inside the open transaction, because a till that could name its own
   * commission could name zero.
   */
  aggregatorId?: string | null;
  /**
   * T13: the platform's own order number, as typed. Trimmed; blank omitted;
   * and dropped entirely without a platform — a reference to nothing is a
   * typo the server would store.
   */
  platformOrderRef?: string;
  /** Whether the waiter has tapped any chip in the tier picker at all. */
  tierTouched: boolean;
  /** The tier chip currently selected — `null` is the "Base prices" chip. */
  tierId: string | null;
  /** Raw text from the covers field, exactly as typed (may be blank). */
  guestCountRaw: string;
  /**
   * D153 — whether anyone tapped a «الكابتن» chip. Untouched sends NO
   * `captainSellerId`, so the SERVER applies the default (the opener, and only
   * when the session is a waiter's); touched sends the choice verbatim, `null`
   * included. A waiter's session never touches it (its row is a read-out):
   * the server refuses a waiter naming a colleague or nobody.
   * Optional: the off-premise dialog has no captain and never sets it.
   */
  captainTouched?: boolean;
  /** D153 — the captain chip selected; `null` is "none". */
  captainSellerId?: string | null;
}

export interface OpenInvoicePayload {
  tableId?: string;
  channel?: string;
  customerName?: string;
  customerPhone?: string;
  deliveryAddress?: string;
  deliveryFee?: number;
  aggregatorId?: string;
  platformOrderRef?: string;
  priceTierId?: string | null;
  guestCount?: number;
  captainSellerId?: string | null;
}

/**
 * Two decimals, half-up — the till's one money rounder for what it SENDS. The
 * server has its own (`roundMoney` in `money.policy.ts`); this keeps a typed
 * `12.345` from reaching the wire as a figure no receipt can print.
 */
export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function resolveOpenInvoicePayload(
  input: OpenInvoicePayloadInput,
): OpenInvoicePayload & Record<string, unknown> {
  const covers = Number.parseInt(input.guestCountRaw, 10);
  const channel = input.channel ?? "DINE_IN";
  const offPremise = channel !== "DINE_IN";
  const fee = Number.parseFloat(input.deliveryFeeRaw ?? "");
  const text = (value: string | undefined) => {
    const trimmed = (value ?? "").trim();
    return trimmed ? trimmed : undefined;
  };
  const optional = (key: string, value: string | undefined) =>
    value === undefined ? {} : { [key]: value };
  return {
    // No `tableId` AT ALL on an off-premise bill — not an empty string, which
    // the DTO would reject as a malformed UUID rather than read as "none".
    ...(offPremise ? {} : { tableId: input.tableId }),
    // Absent is DINE_IN server-side, but it is sent explicitly so the wire says
    // what the waiter chose rather than relying on a default two layers away.
    channel,
    ...optional("customerName", text(input.customerName)),
    ...optional("customerPhone", text(input.customerPhone)),
    ...optional("deliveryAddress", text(input.deliveryAddress)),
    // Only on a DELIVERY, and only when typed: the fee means nothing on the
    // other two channels, and a blank one means "the store's default".
    ...(channel === "DELIVERY" && Number.isFinite(fee) && fee >= 0
      ? { deliveryFee: roundMoney(fee) }
      : {}),
    // F2/T7: on either off-premise channel, and only when one was CHOSEN.
    // "Direct" is the absence of the key, never an explicit null: unlike the
    // tier — where `null` has to out-argue the server's auto-apply — nothing
    // auto-assigns an aggregator, so there is no second answer for `null` to
    // distinguish itself from, and `@IsUUID` would reject it if there were.
    ...(offPremise && input.aggregatorId
      ? { aggregatorId: input.aggregatorId }
      : {}),
    // T13: the platform's order number rides only WITH a platform.
    ...(offPremise && input.aggregatorId
      ? optional("platformOrderRef", text(input.platformOrderRef)?.slice(0, 64))
      : {}),
    ...(input.tierTouched ? { priceTierId: input.tierId } : {}),
    ...(Number.isFinite(covers) && covers > 0 ? { guestCount: covers } : {}),
    // D153: dine-in only (the server refuses a captain on a bag), and only
    // once a chip was touched — see `captainTouched`.
    ...(!offPremise && input.captainTouched
      ? { captainSellerId: input.captainSellerId ?? null }
      : {}),
  };
}
